import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, Loader2, Pencil, RefreshCw, Search, Trash2, AlertTriangle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { poDraftImportApi, type PoCustomer, type PoMapping, type PoVariant } from '@/lib/po-draft-import-api';
import { useSearch } from './use-search';

export function useMappings(storeId: string, token: string) {
  const [items, setItems] = useState<PoMapping[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    setItems([]); setError('');
    if (!storeId || !token) { setLoading(false); return; }
    const ctrl = new AbortController();
    setLoading(true);
    poDraftImportApi.listMappings(storeId, token, ctrl.signal)
      .then((r) => { if (!ctrl.signal.aborted) { setItems(Array.isArray(r) ? r : []); setLoading(false); } })
      .catch((e) => { if (!ctrl.signal.aborted) { setError(e.message || 'Could not load saved mappings'); setLoading(false); } });
    return () => ctrl.abort();
  }, [storeId, token, nonce]);
  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { items, loading, error, reload };
}
type State = ReturnType<typeof useMappings>;

const customerLabel = (c: PoCustomer) => c.name || c.company || c.email || 'Unnamed customer';
const variantLabel = (v: PoVariant) => `${v.productTitle}${v.title && v.title !== 'Default Title' ? ` / ${v.title}` : ''}`;

function Edit({ m, storeId, token, onDone, onCancel }: { m: PoMapping; storeId: string; token: string; onDone: () => void; onCancel: () => void }) {
  const [buyerKey, setBuyerKey] = useState(m.buyerKey);
  const [source, setSource] = useState(m.source);
  const [target, setTarget] = useState<{ id: string; label: string } | null>(
    m.customer ? { id: m.customer.id, label: customerLabel(m.customer) } : m.variant ? { id: m.variant.id, label: variantLabel(m.variant) } : null);
  const [q, setQ] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const isBuyer = m.kind === 'buyer';
  const cs = useSearch<PoCustomer>((s, sig) => poDraftImportApi.customers(storeId, s, token, sig), isBuyer ? q : '', storeId);
  const vs = useSearch<PoVariant>((s, sig) => poDraftImportApi.variants(storeId, s, token, sig), isBuyer ? '' : q, storeId);
  const src = isBuyer ? buyerKey : source;
  const valid = buyerKey.trim() && src.trim() && target;
  const save = async () => {
    if (!valid || !target) return;
    setSaving(true); setErr('');
    try {
      await poDraftImportApi.updateMapping(m.id, { storeId, kind: m.kind, buyerKey: buyerKey.trim(), source: src.trim(), targetId: target.id, confirmed: true }, token);
      if (alive.current) onDone();
    } catch (e: any) { if (alive.current) { setErr(e.message || 'Could not update the mapping'); setSaving(false); } }
  };
  const rows = isBuyer
    ? cs.results.map((c) => ({ id: c.id, label: customerLabel(c), sub: [c.company, c.email].filter(Boolean).join(' · ') }))
    : vs.results.map((v) => ({ id: v.id, label: variantLabel(v), sub: `${v.sku || 'no SKU'} · ${v.productStatus}` }));
  const loading = cs.loading || vs.loading; const error = cs.error || vs.error;
  return (
    <div className="space-y-2 mt-2" data-testid={`form-edit-mapping-${m.id}`}>
      <div className="grid sm:grid-cols-2 gap-2">
        <label className="space-y-0.5"><span className="text-[11px] text-muted-foreground">Buyer key (company, else name)</span>
          <Input value={buyerKey} maxLength={200} disabled={saving} onChange={(e) => setBuyerKey(e.target.value)} data-testid="input-edit-buyer-key" /></label>
        <label className="space-y-0.5"><span className="text-[11px] text-muted-foreground">{isBuyer ? 'Source (same as buyer key)' : 'PDF SKU'}</span>
          <Input value={src} maxLength={200} disabled={saving || isBuyer} onChange={(e) => setSource(e.target.value)} data-testid="input-edit-source" /></label>
      </div>
      <div className="text-[13px] rounded bg-muted/50 px-3 py-2" data-testid="text-edit-target">
        {isBuyer ? 'Customer' : 'Variant'}: <span className="font-medium">{target ? target.label : 'none chosen'}</span>
        {m.status === 'unavailable' && target?.id === m.targetId && <span className="text-amber-600 text-xs ml-2">no longer available in Shopify; choose a new one</span>}
      </div>
      <div className="relative"><Search className="w-3.5 h-3.5 absolute left-2.5 top-3 text-muted-foreground" />
        <Input className="pl-8" placeholder={isBuyer ? 'Search Shopify customers to replace' : 'Search Shopify variants to replace'} value={q} disabled={saving} onChange={(e) => setQ(e.target.value)} data-testid="input-edit-target-search" /></div>
      {loading && <div className="text-xs text-muted-foreground flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Searching…</div>}
      {error && <div className="text-xs text-destructive">{error}</div>}
      <div className="space-y-1 max-h-44 overflow-y-auto">{rows.map((r) => (
        <button key={r.id} type="button" onClick={() => { setTarget({ id: r.id, label: r.label }); setQ(''); }} className="w-full text-left px-2.5 py-1.5 rounded border border-border hover:bg-muted text-[13px]" data-testid={`button-edit-target-${r.id.split('/').pop()}`}>
          <span className="block truncate">{r.label}</span><span className="block text-xs text-muted-foreground truncate">{r.sub}</span></button>))}</div>
      {err && <div className="text-xs text-destructive" data-testid="text-edit-error">{err}</div>}
      <p className="text-[11px] text-muted-foreground">Save to confirm this match. If the new key is already mapped, edit or delete that mapping first.</p>
      <div className="flex gap-2">
        <Button size="sm" disabled={!valid || saving} onClick={save} data-testid="button-save-edit">{saving && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}Save changes</Button>
        <Button size="sm" variant="outline" disabled={saving} onClick={onCancel} data-testid="button-cancel-edit">Cancel</Button>
      </div>
    </div>
  );
}

export function MappingManager({ storeId, token, state, disabled }: { storeId: string; token: string; state: State; disabled: boolean }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [delErr, setDelErr] = useState('');
  const gen = useRef(0);
  useEffect(() => { gen.current++; setEditing(null); setDeleting(null); setBusyId(null); setDelErr(''); }, [storeId, token]);
  const { items, loading, error, reload } = state;
  const del = async (id: string) => {
    const g = gen.current; setBusyId(id); setDelErr('');
    try { await poDraftImportApi.deleteMapping(id, storeId, token); if (g !== gen.current) return; setDeleting(null); reload(); }
    catch (e: any) { if (g === gen.current) setDelErr(e.message || 'Could not delete the mapping'); }
    finally { if (g === gen.current) setBusyId(null); }
  };
  const buyers = items.filter((m) => m.kind === 'buyer'); const skus = items.filter((m) => m.kind === 'sku');
  const group = (title: string, list: PoMapping[]) => (
    <div className="space-y-1.5">
      <div className="text-xs font-semibold text-muted-foreground">{title} ({list.length})</div>
      {list.length === 0 && <div className="text-xs text-muted-foreground">None saved.</div>}
      {list.map((m) => (
        <div key={m.id} className="rounded border border-border px-3 py-2 text-[13px]" data-testid={`row-mapping-${m.id}`}>
          <div className="flex items-start gap-2">
            <div className="flex-1 min-w-0">
              <div className="truncate"><span className="font-mono">{m.kind === 'buyer' ? m.buyerKey : m.source}</span>
                <span className="text-muted-foreground"> to </span>
                <span className="font-medium">{m.customer ? customerLabel(m.customer) : m.variant ? variantLabel(m.variant) : m.targetId}</span></div>
              <div className="text-xs text-muted-foreground truncate">
                {m.kind === 'sku' && <>Buyer key {m.buyerKey} · </>}
                {m.variant && <>{m.variant.sku || 'no SKU'} · current {m.variant.price} · </>}
                {m.customer && <>{[m.customer.company, m.customer.email].filter(Boolean).join(' · ')} · </>}
                <span className={m.status === 'unavailable' ? 'text-amber-600' : 'text-primary'} data-testid={`status-mapping-${m.id}`}>{m.status === 'unavailable' ? 'Unavailable in Shopify' : 'Available'}</span>
              </div>
            </div>
            {deleting === m.id ? (
              <div className="flex gap-1">
                <Button size="sm" variant="destructive" disabled={busyId === m.id} onClick={() => del(m.id)} data-testid={`button-confirm-delete-mapping-${m.id}`}>{busyId === m.id && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}Delete</Button>
                <Button size="sm" variant="outline" disabled={busyId === m.id} onClick={() => setDeleting(null)}>Keep</Button>
              </div>
            ) : (
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" disabled={disabled} aria-label="Edit mapping" onClick={() => { setEditing(editing === m.id ? null : m.id); setDeleting(null); }} data-testid={`button-edit-mapping-${m.id}`}><Pencil className="w-4 h-4" /></Button>
                <Button size="icon" variant="ghost" disabled={disabled} aria-label="Delete mapping" onClick={() => { setDeleting(m.id); setEditing(null); setDelErr(''); }} data-testid={`button-delete-mapping-${m.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            )}
          </div>
          {deleting === m.id && <p className="text-xs text-muted-foreground mt-1">Delete this saved mapping? Future imports will no longer suggest it.</p>}
          {editing === m.id && <Edit m={m} storeId={storeId} token={token} onCancel={() => setEditing(null)} onDone={() => { setEditing(null); reload(); }} />}
        </div>
      ))}
    </div>
  );
  return (
    <section className="glass-panel p-4 space-y-3 max-w-3xl" data-testid="section-mappings">
      <button type="button" className="w-full flex items-center justify-between text-left" onClick={() => setOpen(!open)} data-testid="button-toggle-mappings">
        <span><span className="text-sm font-semibold">Saved matches</span>
          <span className="block text-xs text-muted-foreground">Buyer and SKU matches you chose to save for this store. They are suggestions only and never confirm anything.</span></span>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {open && (
        <div className="space-y-3">
          <div className="flex items-center gap-2"><Button size="sm" variant="outline" disabled={loading} onClick={reload} data-testid="button-refresh-mappings"><RefreshCw className="w-3.5 h-3.5 mr-1" />Refresh</Button>
            {loading && <span className="text-xs text-muted-foreground flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Loading…</span>}</div>
          {error && <div className="text-[13px] text-destructive flex items-center gap-2" data-testid="text-mappings-error"><AlertTriangle className="w-4 h-4" />{error}<Button size="sm" variant="outline" onClick={reload}>Retry</Button></div>}
          {delErr && <div className="text-[13px] text-destructive" data-testid="text-delete-error">{delErr}</div>}
          {!error && loading && items.length === 0 && <div className="space-y-1.5">{[0, 1].map((i) => <div key={i} className="h-12 rounded bg-muted/60 animate-pulse" />)}</div>}
          {!error && !loading && items.length === 0 && <div className="text-sm text-muted-foreground text-center py-4" data-testid="text-no-mappings">Nothing saved yet. After you confirm a customer on an import, you can save it here.</div>}
          {items.length > 0 && <>{group('Buyers', buyers)}{group('SKUs', skus)}</>}
        </div>
      )}
    </section>
  );
}
